import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';

export const DEFAULT_DOMAIN_ID = 'default';
export const DEFAULT_DOMAIN_NAME = 'Default';
export const DEFAULT_DOMAIN_PROJECT_NAME = 'admin'; // TODO Id is still possible to change
export const DEFAULT_DOMAIN_PROJECT_USER_NAME = 'admin'; // TODO Prevent delete user with project role admin

// Domain user roles
export const DOMAIN_USER_DOMAIN_ADMIN_ROLE = 'domain_admin';
export const DOMAIN_USER_SYSTEM_ADMIN_ROLE = 'system_admin';
export const DOMAIN_USER_PROJECT_MEMBER_ROLE = 'project_member';
export const DOMAIN_USER_SERVICE_ACCOUNT_ROLE = 'service_account';

export type DomainUserRole =
  | typeof DOMAIN_USER_DOMAIN_ADMIN_ROLE
  | typeof DOMAIN_USER_SYSTEM_ADMIN_ROLE
  | typeof DOMAIN_USER_PROJECT_MEMBER_ROLE
  | typeof DOMAIN_USER_SERVICE_ACCOUNT_ROLE;

// Domain user project roles
export const PROJECT_ADMIN_USER_ROLE = 'project_admin';
export const PROJECT_READER_USER_ROLE = 'project_reader';
export const PROJECT_NONE_USER_ROLE = null;

// Domain user systemPermissions
export const SYSTEM_ADMIN_PERMISSION = 'admin';
export const SYSTEM_ABGW_PERMISSION = 'abgw';
export const SYSTEM_CLUSTER_PERMISSION = 'cluster';
export const SYSTEM_COMPUTE_PERMISSION = 'compute';
export const SYSTEM_ISCSI_PERMISSION = 'iscsi';
export const SYSTEM_LOGIN_PERMISSION = 'login';
export const SYSTEM_NETWORK_PERMISSION = 'network';
export const SYSTEM_NFS_PERMISSION = 'nfs';
export const SYSTEM_S3_PERMISSION = 's3';
export const SYSTEM_SSH_PERMISSION = 'ssh';
export const SYSTEM_SUPERUSER_PERMISSION = 'superuser';
export const SYSTEM_UPDATES_PERMISSION = 'updates';
export const SYSTEM_VIEWER_PERMISSION = 'viewer';
export const SYSTEM_DEBUG_PERMISSION = 'debug';

export type SystemPermission =
  | typeof SYSTEM_ADMIN_PERMISSION
  | typeof SYSTEM_ABGW_PERMISSION
  | typeof SYSTEM_CLUSTER_PERMISSION
  | typeof SYSTEM_COMPUTE_PERMISSION
  | typeof SYSTEM_ISCSI_PERMISSION
  | typeof SYSTEM_LOGIN_PERMISSION
  | typeof SYSTEM_NETWORK_PERMISSION
  | typeof SYSTEM_NFS_PERMISSION
  | typeof SYSTEM_S3_PERMISSION
  | typeof SYSTEM_SSH_PERMISSION
  | typeof SYSTEM_SUPERUSER_PERMISSION
  | typeof SYSTEM_UPDATES_PERMISSION
  | typeof SYSTEM_VIEWER_PERMISSION
  | typeof SYSTEM_DEBUG_PERMISSION;

export const OMITTED_SYSTEM_PERMISSIONS: SystemPermission[] = [
  SYSTEM_SUPERUSER_PERMISSION,
  SYSTEM_VIEWER_PERMISSION
];

// Domain user domainPermissions
export const DOMAIN_USER_DOMAIN_ADMIN_PERMISSION = 'domain_admin';
export const DOMAIN_USER_IMAGE_UPLOAD_PERMISSION = 'image_upload';
export const DOMAIN_USER_QUOTA_MANAGER_PERMISSION = 'quota_manager';

export type DomainPermissionsMapKey =
  | typeof DOMAIN_USER_DOMAIN_ADMIN_PERMISSION
  | typeof DOMAIN_USER_IMAGE_UPLOAD_PERMISSION;

export type PermissionLocaleEntry = {
  name: string;
  title: string;
  description: string;
};

export const SYSTEM_PERMISSIONS = computedLocale<Record<SystemPermission, PermissionLocaleEntry>>(() => ({
  [SYSTEM_ADMIN_PERMISSION]: {
    name: SYSTEM_ADMIN_PERMISSION,
    title: i18n.t('System administrator'),
    description: i18n.t('Can create and manage domains, projects, and services.'),
  },
  [SYSTEM_SUPERUSER_PERMISSION]: {
    name: SYSTEM_SUPERUSER_PERMISSION,
    title: i18n.t('Superuser'),
    description: i18n.t('Can create and manage resources.'),
  },
  [SYSTEM_ABGW_PERMISSION]: {
    name: SYSTEM_ABGW_PERMISSION,
    title: i18n.t('ABGW'),
    description: i18n.t('Can create and manage Backup Gateway.'),
  },
  [SYSTEM_CLUSTER_PERMISSION]: {
    name: SYSTEM_CLUSTER_PERMISSION,
    title: i18n.t('Cluster'),
    description: i18n.t('Can create the storage cluster, join nodes to it, and manage storage node disks.'),
  },
  [SYSTEM_COMPUTE_PERMISSION]: {
    name: SYSTEM_COMPUTE_PERMISSION,
    title: i18n.t('Compute'),
    description: i18n.t('Can create and manage the compute cluster.'),
  },
  [SYSTEM_ISCSI_PERMISSION]: {
    name: SYSTEM_ISCSI_PERMISSION,
    title: i18n.t('ISCSI'),
    description: i18n.t('Can create and manage iSCSI targets, LUNs and CHAP users.'),
  },
  [SYSTEM_LOGIN_PERMISSION]: {
    name: SYSTEM_LOGIN_PERMISSION,
    title: i18n.t('Login'),
    description: i18n.t('Can log in to the admin panel.'),
  },
  [SYSTEM_NETWORK_PERMISSION]: {
    name: SYSTEM_NETWORK_PERMISSION,
    title: i18n.t('Network'),
    description: i18n.t('Can modify networks and traffic types.'),
  },
  [SYSTEM_NFS_PERMISSION]: {
    name: SYSTEM_NFS_PERMISSION,
    title: i18n.t('NFS'),
    description: i18n.t('Can create and manage NFS volumes and exports.'),
  },
  [SYSTEM_S3_PERMISSION]: {
    name: SYSTEM_S3_PERMISSION,
    title: i18n.t('S3'),
    description: i18n.t('Can create and manage the S3 cluster.'),
  },
  [SYSTEM_SSH_PERMISSION]: {
    name: SYSTEM_SSH_PERMISSION,
    title: i18n.t('SSH'),
    description: i18n.t('Can add and remove SSH keys for cluster nodes access.'),
  },
  [SYSTEM_UPDATES_PERMISSION]: {
    name: SYSTEM_UPDATES_PERMISSION,
    title: i18n.t('Update'),
    description: i18n.t('Can install updates.'),
  },
  [SYSTEM_VIEWER_PERMISSION]: {
    name: SYSTEM_VIEWER_PERMISSION,
    title: i18n.t('Viewer'),
    description: i18n.t('Can view resources.'),
  },
  [SYSTEM_DEBUG_PERMISSION]: {
    name: SYSTEM_DEBUG_PERMISSION,
    title: i18n.t('Debug'),
    description: i18n.t('Can access the cluster in the restricted mode for troubleshooting.'),
  }
}));

export const DOMAIN_PERMISSIONS = computedLocale<Record<DomainPermissionsMapKey, PermissionLocaleEntry>>(() => ({
  [DOMAIN_USER_DOMAIN_ADMIN_PERMISSION]: {
    name: DOMAIN_USER_DOMAIN_ADMIN_PERMISSION,
    title: i18n.t('Admin'),
    description: i18n.t('Can create and manage domains, projects, and services.'),
  },
  [DOMAIN_USER_IMAGE_UPLOAD_PERMISSION]: {
    name: DOMAIN_USER_IMAGE_UPLOAD_PERMISSION,
    title: i18n.t('Upload'),
    description: i18n.t('Can upload images.'),
  },
}));

export const USER_ROLES = computedLocale<Record<DomainUserRole, PermissionLocaleEntry>>(() => ({
  [DOMAIN_USER_DOMAIN_ADMIN_ROLE]: {
    name: DOMAIN_USER_DOMAIN_ADMIN_ROLE,
    title: i18n.t('Domain administrator'),
    description: i18n.t('Can create and manage services in the assigned domain.'),
  },
  [DOMAIN_USER_SYSTEM_ADMIN_ROLE]: {
    name: DOMAIN_USER_SYSTEM_ADMIN_ROLE,
    title: i18n.t('System administrator'),
    description: i18n.t('Can create and manage domains, projects, and services.'),
  },
  [DOMAIN_USER_PROJECT_MEMBER_ROLE]: {
    name: DOMAIN_USER_PROJECT_MEMBER_ROLE,
    title: i18n.t('Project member'),
    description: i18n.t('Access depends on project permissions: full access allows managing services, while read-only access allows viewing only.'),
  },
  [DOMAIN_USER_SERVICE_ACCOUNT_ROLE]: {
    name: DOMAIN_USER_SERVICE_ACCOUNT_ROLE,
    title: i18n.t('Service user'),
    description: i18n.t('Can be used by third-party applications to access the system API.'),
  },
}));

export const MIN_IDP_REQUEST_TIMEOUT = 5;
